import type { CSSProperties } from "react";
import React, { memo, useMemo } from "react";

export type RulebookMeterBarSize = "segment" | "hairline" | "panel" | "progress" | "tile";

type RulebookMeterBarProps = {
  fill: number;
  color?: string;
  size: RulebookMeterBarSize;
  "data-testid"?: string;
  fillTestId?: string;
};

const PERCENT = 100;

const sizeClasses: Record<RulebookMeterBarSize, { track: string; fill: string }> = {
  segment: {
    track: "overflow-hidden h-1.5 rounded-[1px] bg-gray-5",
    fill: "h-full transition-[width] duration-700",
  },
  hairline: {
    track: "overflow-hidden h-1 rounded-full bg-gray-5",
    fill: "h-full transition-[width] duration-700 rounded-full",
  },
  panel: {
    track: "relative h-2 overflow-hidden rounded-[8px] bg-gray-5",
    fill: "h-full rounded-[8px] transition-[width] duration-700",
  },
  progress: {
    track: "h-1.5 overflow-hidden rounded-[6px] bg-gray-6",
    fill: "h-full rounded-[6px]",
  },
  tile: {
    track: "h-1.5 overflow-hidden rounded-full bg-gray-4",
    fill: "h-full rounded-full",
  },
};

const RulebookMeterBar = memo(({ fill, color, size, "data-testid": testId = "RulebookMeterBar", fillTestId }: RulebookMeterBarProps) => {
  const clampedFill = Math.min(1, Math.max(0, fill));
  const classes = sizeClasses[size];
  const fillStyle = useMemo<CSSProperties>(
    () => ({ width: `${clampedFill * PERCENT}%`, ...(color ? { backgroundColor: color } : {}) }),
    [clampedFill, color],
  );

  return (
    <div className={classes.track} data-testid={testId}>
      <div className={`${classes.fill}${color ? "" : " bg-gray-11"}`} style={fillStyle} data-testid={fillTestId} />
    </div>
  );
});

RulebookMeterBar.displayName = "RulebookMeterBar";

export default RulebookMeterBar;
